import { HttpClient } from "@/shared/lib/http-client"
import { env } from "@/config/env"
import { getAttributionPayload } from "@/features/analytics"
import type { SubscriptionResult } from "../types"

export interface CreatePaypalSubscriptionParams {
  planId: string
  email: string
  /** PayPal subscription id returned by the PayPal SDK on approval. */
  paypalSubscriptionId: string
  orderId?: string
}

export interface FinalizePaypalSubscriptionParams {
  subscriptionId: string
  email: string
  firebaseId?: string
  plan?: string
}

export interface IPaypalService {
  readonly isConfigured: boolean
  createSubscription(params: CreatePaypalSubscriptionParams): Promise<SubscriptionResult>
  finalizeSubscription(params: FinalizePaypalSubscriptionParams): Promise<{ success: boolean }>
}

/**
 * PayPal subscription backend client.
 * - Create:   POST {base}/paypal/subscription
 * - Finalize: POST {base}/rec/subscription-finalize-paypal
 */
export class PaypalService extends HttpClient implements IPaypalService {
  readonly isConfigured: boolean

  constructor(baseURL: string) {
    super({ baseURL })
    this.isConfigured = Boolean(baseURL)
  }

  async createSubscription(params: CreatePaypalSubscriptionParams): Promise<SubscriptionResult> {
    const data = await this.post<SubscriptionResult>("/paypal/subscription", {
      plan_id: params.planId,
      email: params.email,
      subscription_id: params.paypalSubscriptionId,
      order_id: params.orderId ?? null,
      attribution: getAttributionPayload(),
    })
    if (data.error) throw new Error(data.error)
    return data
  }

  async finalizeSubscription(
    params: FinalizePaypalSubscriptionParams,
  ): Promise<{ success: boolean }> {
    return this.post("/rec/subscription-finalize-paypal", {
      subscription_id: params.subscriptionId,
      email: params.email,
      firebase_id: params.firebaseId,
      plan: params.plan,
      attribution: getAttributionPayload(),
    })
  }
}

export const paypalService: IPaypalService = new PaypalService(env.VITE_PAYMENT_API_URL)
